import { useEffect, useState } from 'react';
import { getCompanies } from 'api';
import { hasAnyParkingLocation } from 'api/canpark/parkingLocations/hasAnyParkingLocation';

// Several CANpark tours (record a plate, monitor a site, register a plate) need at
// least one parking lot to exist — without one they can't proceed and fail partway.
// This checks whether ANY of the user's companies has a lot so the checklist can
// disable those tours until one does.
//
// Correct AND cheap: one call resolves the user's companies, then a single backend
// existence query (HasAnyParkingLocation) checks them all at once — instead of one
// getParkingLocations request per company, which stormed the API for accounts with
// hundreds of companies. Only runs when `enabled` (a lot-gated tour is offered), so
// we don't fetch CANpark data for everyone.
export function useTicketingTourReady(enabled: boolean): {
	hasLot: boolean;
	checked: boolean;
} {
	const [hasLot, setHasLot] = useState(false);
	const [checked, setChecked] = useState(false);

	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		(async () => {
			try {
				const customers = (await getCompanies()) as
					| { Id: string }[]
					| undefined;
				if (!customers?.length) return;
				const exists = await hasAnyParkingLocation(
					customers.map((c) => c.Id),
				);
				if (!cancelled) setHasLot(exists);
			} catch {
				// On error leave hasLot false — safer to block than let it fail.
			} finally {
				if (!cancelled) setChecked(true);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [enabled]);

	return { hasLot, checked };
}
